import { getPictureUrl } from 'core/api/pictures';
import Carousel from 'core/components/carousel';
import { NextButton, PreviousButton } from 'core/components/carousel/controls';
import { useLinks } from 'core/components/linksContext';
import { useSettings } from 'core/components/settingsContext';
import { title } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { isMobile } from 'core/utils';
import React from 'react';
import { Account, Lecturer } from 'site/graphql';

type IProductTeachersProps = {
    lecturers: (Pick<Lecturer, 'id' | 'education' | 'skill' | 'about'> & {
        account: Pick<Account, 'id' | 'pictureId' | 'firstName' | 'lastName'>;
    })[];
} & WithStyles<typeof styles>;

const ProductTeachers: React.FC<IProductTeachersProps> = ({ classes, lecturers = [] }) => {
    const links = useLinks();
    const settings = useSettings();

    const renderLecturerTitle = (lecturer: IProductTeachersProps['lecturers'][0]) => {
        return (
            <h3 className={classes.name}>
                <links.teacherDetail.Link target="_blank" to={links.teacherDetail.url(lecturer.id)}>
                    {lecturer.account.firstName} {lecturer.account.lastName}
                </links.teacherDetail.Link>
            </h3>
        );
    };

    const renderLecturerFooter = (lecturer: IProductTeachersProps['lecturers'][0]) => {
        return (
            <div className={classes.footer}>
                <links.teacherDetail.Link target="_blank" to={links.teacherDetail.url(lecturer.id)}>
                    Все курсы преподавателя
                </links.teacherDetail.Link>
            </div>
        );
    };

    const renderLecturerPicture = (lecturer: IProductTeachersProps['lecturers'][0]) => {
        return (
            <links.teacherDetail.Link
                style={{
                    background: `url("${getPictureUrl(
                        lecturer.account.pictureId,
                        '300x300',
                        settings,
                    )}") center center / cover no-repeat`,
                }}
                className={classes.picture}
                target="_blank"
                to={links.teacherDetail.url(lecturer.id)}
            />
        );
    };

    const renderOneTeacher = () => {
        const lecturer = lecturers[0];

        return (
            <>
                {renderLecturerTitle(lecturer)}
                <div className={classes.oneTeacherContainer}>
                    {renderLecturerPicture(lecturer)}
                    <div dangerouslySetInnerHTML={{ __html: lecturer.skill }} className={classes.oneTeacherSkill} />
                </div>
                {renderLecturerFooter(lecturer)}
            </>
        );
    };

    const renderTeachers = () => {
        const customControlStyles = {
            fill: 'black',
            opacity: undefined,
            display: lecturers.length > 1 ? 'block' : 'none',
        };

        if (lecturers.length === 1) {
            return renderOneTeacher();
        }

        return (
            <Carousel
                swiping
                slidesToShow={isMobile(600) ? 1 : 2}
                speed={250}
                dragging={false}
                renderBottomCenterControls={null}
                classNameLi={classes.carouselLi}
                renderCenterLeftControls={props => (
                    <PreviousButton {...props} customStyles={customControlStyles} darkenWhenBoundary />
                )}
                renderCenterRightControls={props => (
                    <NextButton {...props} customStyles={customControlStyles} darkenWhenBoundary />
                )}
                decoratorStyleCenterLeftControls={{ left: isMobile(600) ? 5 : 0 }}
                decoratorStyleCenterRightControls={{ right: isMobile(600) ? 5 : 0 }}
            >
                {lecturers.map(lecturer => (
                    <div key={lecturer.id}>
                        {renderLecturerTitle(lecturer)}
                        {renderLecturerPicture(lecturer)}
                        {renderLecturerFooter(lecturer)}
                    </div>
                ))}
            </Carousel>
        );
    };

    return (
        <div className={classes.container}>
            <h2 className={classes.title}>Преподаватели</h2>
            {renderTeachers()}
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        container: {
            marginTop: 1.5 + 'rem',
        },
        oneTeacherContainer: {
            display: 'flex',

            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
            },
        },
        oneTeacherSkill: {
            padding: '0 15px',

            [theme.breakpoints.down('sm')]: {
                padding: '15px 0',
            },
        },
        name: {
            ...title({ size: 'h3' }),
            marginBottom: 1 + 'rem',

            '& a': {
                color: theme.colors.textBase,
                textTransform: 'uppercase',

                '&:hover': {
                    color: theme.colors.linkOldHover,
                },
            },
        },
        footer: {
            textAlign: 'left',
            marginTop: 1 + 'rem',
        },
        picture: {
            maxWidth: 300,
            maxHeight: 300,
            display: 'block',

            [theme.breakpoints.down('sm')]: {
                maxWidth: 200,
                maxHeight: 200,
            },

            '&:after': {
                content: '""',
                display: 'block',
                width: 300,
                height: 300,

                [theme.breakpoints.down('sm')]: {
                    width: 200,
                    height: 200,
                },
            },
        },
        carouselLi: {
            display: 'flex !important',
            justifyContent: 'center',
        },
        title: {
            ...title({ size: 'h2' }),
            marginBottom: 0.75 + 'rem',

            [theme.breakpoints.down('sm')]: {
                textAlign: 'center',
                fontSize: 1.25 + 'rem',
            },
        },
    });

export default injectSheet(styles, 'ProductTeachers')(ProductTeachers);
